import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import api from "../../api";
import { Helmet } from "react-helmet-async";

const helmetNode = (
  <Helmet>
    <title>Mon profil | LMFP Photography</title>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

const Profile = () => {
  const { authLoading, isAuthenticated } = useAuth();

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState("");

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      setError("");
      const response = await api.get("/users/me/profile");
      setProfile(response.data);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement du profil.");
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) fetchProfile();
    if (!authLoading && !isAuthenticated) setProfileLoading(false);
  }, [authLoading, isAuthenticated]);

  const formattedBirthday = profile?.birthday
    ? new Date(profile.birthday).toLocaleDateString("fr-BE")
    : "Non renseignée";

  const formattedCreatedAt = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString("fr-BE")
    : "Inconnu";

  const formattedUpdatedAt = profile?.updatedAt
    ? new Date(profile.updatedAt).toLocaleDateString("fr-BE")
    : "Inconnu";

  const avatarUrl = getImageUrl(profile?.avatar);

  if (authLoading || profileLoading) {
    return (
      <>
        {helmetNode}
        <div className="flex min-h-screen items-center justify-center bg-gray-900 text-white">
          <p className="text-xl font-semibold">Chargement du profil...</p>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {helmetNode}
        <div className="flex min-h-screen items-center justify-center bg-gray-900 px-4">
          <div className="rounded-lg border border-red-500 bg-gray-800 px-6 py-4 text-center text-red-400">
            Vous devez être connecté pour voir votre profil.
          </div>
        </div>
      </>
    );
  }

  if (error || !profile) {
    return (
      <>
        {helmetNode}
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-900 px-4">
          <div className="rounded-lg border border-red-500 bg-gray-800 px-6 py-4 text-center text-red-400">
            {error || "Profil introuvable."}
          </div>
          <button
            onClick={fetchProfile}
            className="mt-4 rounded-lg bg-orange-500 px-5 py-3 font-semibold text-white transition hover:bg-orange-600"
          >
            Réessayer
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {helmetNode}
      <div className="min-h-screen bg-gray-900 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <div className="overflow-hidden rounded-2xl border border-gray-700 bg-gray-800 text-white shadow-xl">
            <div className="border-b border-gray-700 px-6 py-8 text-center">
              <div className="mb-4 flex justify-center">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="h-32 w-32 rounded-full border-4 border-gray-700 object-cover shadow-lg"
                  />
                ) : (
                  <div className="flex h-32 w-32 items-center justify-center rounded-full border-4 border-gray-600 bg-gray-700 text-sm text-gray-300">
                    Pas d'avatar
                  </div>
                )}
              </div>

              <h1 className="text-3xl font-bold">
                {profile.firstname} {profile.lastname}
              </h1>

              <p className="mt-2 text-gray-400">{profile.email}</p>

              {profile.location && (
                <p className="mt-2 text-sm text-gray-400">
                  📍 {profile.location}
                </p>
              )}

              {profile.role && (
                <p className="mt-3 inline-block rounded-full bg-gray-700 px-4 py-1 text-sm font-semibold text-orange-400">
                  {profile.role}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 px-6 py-8 md:grid-cols-2">
              <div className="rounded-xl bg-gray-900 p-5 shadow">
                <h2 className="mb-4 text-lg font-bold text-orange-400">
                  Informations personnelles
                </h2>
                <div className="space-y-3 text-gray-300">
                  <p><span className="font-semibold text-white">Prénom :</span> {profile.firstname || "Non renseigné"}</p>
                  <p><span className="font-semibold text-white">Nom :</span> {profile.lastname || "Non renseigné"}</p>
                  <p><span className="font-semibold text-white">Email :</span> {profile.email || "Non renseigné"}</p>
                  <p><span className="font-semibold text-white">Localité :</span> {profile.location || "Non renseignée"}</p>
                  <p><span className="font-semibold text-white">Date de naissance :</span> {formattedBirthday}</p>
                </div>
              </div>

              <div className="rounded-xl bg-gray-900 p-5 shadow">
                <h2 className="mb-4 text-lg font-bold text-orange-400">
                  Activité
                </h2>
                <div className="space-y-3 text-gray-300">
                  <p><span className="font-semibold text-white">Nombre de commentaires :</span> {profile.commentsCount || 0}</p>
                  <p><span className="font-semibold text-white">Compte créé le :</span> {formattedCreatedAt}</p>
                  <p><span className="font-semibold text-white">Dernière mise à jour :</span> {formattedUpdatedAt}</p>
                  <p><span className="font-semibold text-white">Rôle :</span> {profile.role || "Non renseigné"}</p>
                </div>
              </div>
            </div>

            <div className="px-6 pb-8">
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Link
                  to={`/user/edit/${profile.id}`}
                  className="rounded-lg bg-blue-500 px-6 py-3 text-center font-bold text-white transition hover:bg-blue-700"
                >
                  Modifier le profil
                </Link>
                <Link
                  to={`/user/editPassword/${profile.id}`}
                  className="rounded-lg bg-red-500 px-6 py-3 text-center font-bold text-white transition hover:bg-red-700"
                >
                  Modifier le mot de passe
                </Link>
              </div>
            </div>

            <div className="border-t border-gray-700 px-6 py-8">
              <h2 className="mb-6 text-2xl font-bold text-orange-400">
                Mes commentaires récents
              </h2>
              {profile.comments && profile.comments.length > 0 ? (
                <div className="space-y-4">
                  {profile.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="rounded-xl border border-gray-700 bg-gray-900 p-4"
                    >
                      <p className="mb-3 text-gray-200">{comment.content}</p>
                      <div className="flex flex-col gap-2 text-sm text-gray-400 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          Post lié :{" "}
                          <span className="font-medium text-white">
                            {comment.postTitle || `Post #${comment.postId}`}
                          </span>
                        </span>
                        {comment.postSlug && (
                          <Link
                            to={`/posts/${comment.postSlug}`}
                            className="font-medium text-orange-400 hover:text-orange-300"
                          >
                            Voir le post
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">
                  Vous n'avez encore publié aucun commentaire.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;